import * as Sentry from '@sentry/gatsby';
import axios from "axios"
import { useEffect, useMemo, useRef, useState } from 'react';
import { object, string } from 'yup';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import MarketoForm from '~globals/Marketo/marketoForm';
import { EMAIL_REGEXP  } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppSelector } from '~hooks/useRedux';
import { useCookie } from '~hooks/useStorageState';
import BannerClock from '~images/banners/banner-clock.svg';
import Close from '~images/common/close.svg';
import Reminder from './Reminder';

export const validate = (email: string) => {
    const emailRegex: RegExp = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i
    return emailRegex.test(email)
  }  

const DOMAIN_URL = `https://www.${process.env.GATSBY_SUBDOMAIN}ehealthinsurance.com`;

export const PreOepBannerDataSchema = object({
    email: string().matches(EMAIL_REGEXP, 'Please enter a valid email address').required('Required'),
});

type PreOepBannerProps = {
    setPreOEP: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PreOepBanner = ({ setPreOEP }: PreOepBannerProps) => {
    const leadForm = useAppSelector(({ global }) => global.preOepStateConfig);
    const AFFILIATE_ID = useRef<string>('affiliateID1131');

    const [cookie, setCookie] = useCookie('leadForm.closed');
    const { location } = usePageContext();

    const isStatesPage = location.pathname.includes('-health-insurance/');
    const [isLoading, setIsLoading] = useState(false);
    const [wasSubmitted, setWasSubmitted] = useState(Boolean(cookie));
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
    const [email, setEmail] = useState<string>('');
    const [leadEmail, setLeadEmail] = useState<string | null>();
    const isNotValid = useMemo(() => isDirty && !validate(email), [isDirty, email])

    const mktoForm = new MarketoForm(+leadForm.marketoFormId);

    useEffect(() => {
        setTimeout(() => {
            mktoForm.init();
        }, 1000);
    }, []);

    useEffect(() => {
        if (wasSubmitted) {
            const timer = setTimeout(() => {
                // we wait to set store the email to cookies until this point, other the component re-renders and hides the banner (as expected) before showing the confirmation message for 5s as desired
                setCookie('true');
                setPreOEP(() => false);
                setWasSubmitted(false);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [leadEmail, wasSubmitted]);

    const handleSubmit = async (e: React.MouseEvent) => {
        e.preventDefault();

        if (isLoading) return;
        setIsLoading(true);

        const emailStatus = isStatesPage ? 'Active' : undefined;

        try {
            mktoForm.mktoFormSubmit(email, AFFILIATE_ID.current, emailStatus);
            
            const data = new URLSearchParams();
            data.append("leadRequest.email", email);
            data.append("leadRequest.trackingStatus", leadForm.trackingStatus);

            const REQUEST_URL = `${DOMAIN_URL}/ehi/ifp/oep-lead!saveForNewHouse`;
            const response = await axios.post(REQUEST_URL, data, {
                headers: {
                    'content-type': 'application/x-www-form-urlencoded',
                },
            });
            
            if (response.status === 200) {
                fireTrackEvent({
                    page: { action: 'notify me' },
                    shop: { email },
                    events: ['save_results'],
                  });
            }

            setWasSubmitted(true);
            setLeadEmail(email);
        } catch (error: unknown) {
            let sentryError;
            if (typeof error === 'string') {
                sentryError = new Error(error);
                console.error('OEP Banner Lead error:', error);
            } else if (error instanceof Error) {
                sentryError = new Error(error.message);
                console.error('OEP Banner error:', error.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'OEPBannerLeadResponseData',
                        event: 'createLead',
                    },
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    
  if (!isModalOpen)
    return null

  if (!leadForm)
    return null

    return (
        <div className="bg-surface-primary-darker duration-500 ease-in-out">
        <div className="flex w-full flex justify-center text-white font-poppins pt-4 pb-1 px-8 sm:px-0 sm:gap-3">
          {wasSubmitted
            ? (
                <Reminder setIsModalOpen={setIsModalOpen} />
              )
            : (
                <>
                  <img className="mb-3 hidden sm:block" src={BannerClock} alt="Clock Icon" />
                  <div className="flex items-center gap-3 flex-col xl:flex-row">
                    <div className="font-semibold text-center text-lg m-0 pl-2 pr-12 ss:pl-0 ss:pr-0 ss:mr-8 md:px-0 xl:mb-6">{leadForm.title}</div>
                    <div className="">
                      <div className="flex gap-3 ">
                        <input
                          value={email}
                          onChange={({ target }) => setEmail(target.value)}
                          type="email"
                          placeholder="Enter your email"
                          className={`h-11 text-black border border-white rounded-md p-3 w-52 ${isNotValid && 'border-rose-500'} sm:w-fit`}
                          onBlur={() => setIsDirty(() => true)}
                        />
                        <input
                          className={`h-11 w-28 border-2 border-white rounded-full font-bold cursor-pointer bg-[#24a400] ${isNotValid && 'cursor-not-allowed'} sm:w-44`}
                          type="button"
                          disabled={isNotValid}
                          value={leadForm.actionText}
                          onClick={handleSubmit}
                        />
                      </div>
                      <div className={`flex w-full text-xs justify-center flex-${isNotValid ? 'col' : 'row'} sm:flex-row md:justify-${isNotValid ? 'between' : 'end'}`}>
                        {isNotValid && <div className="text-red-email-error">Please enter a valid email address.</div>}
                        <div className="mt-2 mr-1 text-center mb-2 md:mb-0" dangerouslySetInnerHTML={{ __html: leadForm.additionalContent }} />
                      </div>
                    </div>
                  </div>
                </>
              )}
          <div className="block absolute right-[10px] sm:ml-8 sm:flex sm:items-start sm:right-0 sm:static">
            <img src={Close} className="cursor-pointer" alt="Close" width="24" onClick={() => setIsModalOpen(() => false)} />
          </div>
        </div>
      </div>
    );
};
